import { useEffect, useState } from 'react';
import facebookAvatar from '@/assets/images/avatar-fb.png';
// Import channel images
import instagramAvatar from '@/assets/images/avatar-ig.png';
import linkedinAvatar from '@/assets/images/avatar-linkedin.png';
import useWorkflowStore from '@/stores/WorkflowStore';
import { Edit2, GitBranch, Loader2, SettingsIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner'; // Assuming sonner is used for notifications
import { useShallow } from 'zustand/react/shallow';

import {
  fetchWorkflowInstance,
  updateWorkflowInstance,
} from '@/hooks/workflow';
// Assuming fetchWorkflowInstance is exported
import { Button } from '@/components/ui/button'; // Adjust path as needed

import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

import ChannelSelectionDialog from '../ChannelSelectionDialog'; // Reuse channel dialog
import EditWorkflowDetailsDialog from '../EditWorkflowDetailsDialog'; // Reuse details dialog
import WorkflowSettingsDialog from '../WorkflowSettingsDialog'; // Reuse settings dialog

import UpdateWorkflow from './UpdateWorkflow';
// import UpdateWorkflow from './UpdateWorkflow'; // Component to be created
import WorkflowEditor from './WorkflowEditor'; // Component to be created

const EditWorkflowPage = () => {
  const { workflowId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [isChannelModalOpen, setIsChannelModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [tempChannels, setTempChannels] = useState([]);
  const [isPendingActivateWorkflow, setIsPendingActivateWorkflow] =
    useState(false);

  const {
    tempNodes,
    tempEdges,
    tempTrigger,
    workflowName,
    workflowDescription,
    channels,
    isActive,
    reEnrollment,
    enrollExisting,
    removeIfIneligible,
    setWorkflowName,
    setWorkflowDescription,
    setChannels,
    setIsActive,
    setReEnrollment,
    setEnrollExisting,
    setRemoveIfIneligible,
    setWorkflowForEditing,
    resetTempWorkflowState,
    workflowFirstActivatedAt,
  } = useWorkflowStore(
    useShallow((state) => ({
      tempNodes: state.tempNodes,
      tempEdges: state.tempEdges,
      tempTrigger: state.tempTrigger,
      workflowName: state.workflowName,
      workflowDescription: state.workflowDescription,
      channels: state.channels,
      isActive: state.isActive,
      reEnrollment: state.reEnrollment,
      enrollExisting: state.enrollExisting,
      removeIfIneligible: state.removeIfIneligible,
      setWorkflowName: state.setWorkflowName,
      setWorkflowDescription: state.setWorkflowDescription,
      setChannels: state.setChannels,
      setIsActive: state.setIsActive,
      setReEnrollment: state.setReEnrollment,
      setEnrollExisting: state.setEnrollExisting,
      setRemoveIfIneligible: state.setRemoveIfIneligible,
      setWorkflowForEditing: state.setWorkflowForEditing,
      resetTempWorkflowState: state.resetTempWorkflowState,
      workflowFirstActivatedAt: state.workflowFirstActivatedAt,
    }))
  );

  // Channel icons mapping
  const channelIcons = {
    instagram: {
      icon: (
        <img src={instagramAvatar} alt='Instagram' width={24} height={24} />
      ),
      label: 'Instagram',
    },
    facebook: {
      icon: <img src={facebookAvatar} alt='Facebook' width={24} height={24} />,
      label: 'Facebook',
    },
    linkedin: {
      icon: <img src={linkedinAvatar} alt='LinkedIn' width={24} height={24} />,
      label: 'LinkedIn',
    },
  };

  useEffect(() => {
    // Reset state when component mounts or workflowId changes
    resetTempWorkflowState();
    setIsLoading(true);
    setError(null);

    const loadWorkflow = async () => {
      if (!workflowId) {
        setError('No workflow ID provided.');
        setIsLoading(false);
        return;
      }
      try {
        // Assuming fetchWorkflowInstance returns the data directly
        const workflowData = await fetchWorkflowInstance(workflowId);
        if (workflowData?.data) {
          setWorkflowForEditing(workflowData.data); // Pass the nested data object
        } else {
          throw new Error('Workflow data not found or in unexpected format.');
        }
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching workflow instance:', err);
        setError(
          err.response?.data?.message ||
            err.message ||
            'Failed to load workflow data.'
        );
        toast.error(
          `Failed to load workflow: ${
            err.response?.data?.message || err.message
          }`
        );
        setIsLoading(false);
        navigate('/workflows');
      }
    };

    loadWorkflow();

    // Cleanup function to reset state when unmounting
    return () => {
      resetTempWorkflowState();
    };
  }, [workflowId, setWorkflowForEditing, resetTempWorkflowState, navigate]);

  const handleEditSave = () => {
    setIsEditing(false);
  };

  const handleSettingsSave = (settings) => {
    if (settings) {
      const settingsData = {
        ...settings,
        ...(isPendingActivateWorkflow ? { is_active: true } : {}),
        ...(settings.is_active !== undefined
          ? { is_active: settings.is_active }
          : {}),
      };
      toast.promise(updateWorkflowInstance(workflowId, settingsData), {
        loading: 'Updating workflow settings...',
        success: () => {
          setReEnrollment(settings.re_enrollment);
          setEnrollExisting(settings.enroll_existing);
          setRemoveIfIneligible(settings.remove_if_ineligible);
          if (isPendingActivateWorkflow) {
            setIsActive(true);
            setIsPendingActivateWorkflow(false);
          }
          setIsSettingsModalOpen(false);
          return 'Workflow settings updated successfully';
        },
        error: 'Failed to update workflow settings',
      });
    }
  };

  const handleChannelToggle = (channel) => {
    setTempChannels((prevChannels) =>
      prevChannels.includes(channel)
        ? prevChannels.filter((c) => c !== channel)
        : [...prevChannels, channel]
    );
  };

  const handleChannelModalOpen = () => {
    setTempChannels([...channels]);
    setIsChannelModalOpen(true);
  };

  const handleChannelModalSave = () => {
    setChannels(tempChannels);
    setIsChannelModalOpen(false);
  };

  const getChannelIcon = (channel) => {
    const channelData = channelIcons[channel];
    return channelData ? channelData.icon : null;
  };

  const handleActivateWorkflow = (checked) => {
    if (checked === isActive) return;
    if (checked === true) {
      setIsPendingActivateWorkflow(true);
      setIsSettingsModalOpen(true);
    } else {
      toast.promise(
        updateWorkflowInstance(workflowId, { is_active: checked }),
        {
          loading: 'Updating workflow status...',
          success: () => {
            setIsActive(checked);
            return 'Workflow status updated successfully';
          },
          error: 'Failed to update workflow status',
        }
      );
    }
  };

  if (isLoading) {
    return (
      <div className='flex h-[calc(100vh-100px)] w-full items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
        <span className='ml-2 text-lg'>Loading Workflow...</span>
      </div>
    );
  }

  if (error) {
    return null;
  }

  return (
    <div className='mx-auto h-full p-4'>
      {/* Header Area */}
      <div className='flex flex-col gap-4'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <div className='rounded-md bg-blue-50 p-1.5 text-blue-600'>
              <GitBranch className='h-5 w-5' />
            </div>
            <div className='flex items-center gap-2'>
              <h1 className='max-w-[400px] truncate text-xl font-semibold tracking-tight'>
                {workflowName}
              </h1>
              <Button
                variant='ghost'
                size='icon'
                className='h-6 w-6 p-0 hover:bg-muted'
                onClick={() => setIsEditing(true)}
              >
                <Edit2 className='h-4 w-4' />
              </Button>
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <div className='flex items-center gap-1 rounded-lg border bg-muted p-1'>
              <button
                onClick={() => handleActivateWorkflow(true)}
                className={`rounded-md px-3 py-1 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? 'bg-green-600 text-white shadow-sm'
                    : 'text-muted-foreground hover:bg-muted/50'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => handleActivateWorkflow(false)}
                className={`rounded-md px-3 py-1 text-sm font-medium transition-colors duration-200 ${
                  !isActive
                    ? 'bg-background text-muted-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted/50'
                }`}
              >
                Inactive
              </button>
            </div>
            <Button
              variant='outline'
              size='sm'
              className='gap-2'
              onClick={handleChannelModalOpen}
            >
              {channels.length > 0 ? (
                <div className='flex items-center gap-1'>
                  {channels.map((channel) => (
                    <span key={channel}>{getChannelIcon(channel)}</span>
                  ))}
                </div>
              ) : (
                'Select Channels'
              )}
            </Button>
            <Button
              variant='outline'
              size='sm'
              className='gap-2'
              onClick={() => setIsSettingsModalOpen(true)}
            >
              <SettingsIcon className='h-4 w-4' />
            </Button>
            <UpdateWorkflow
              workflowId={workflowId}
              nodes={tempNodes}
              edges={tempEdges}
              disabled={!tempNodes || tempNodes.length === 0}
              className='flex items-center gap-2'
            />
          </div>
        </div>

        <Card className='h-[calc(100vh-120px)]'>
          <WorkflowEditor />
        </Card>
      </div>

      {/* Dialogs */}

      <EditWorkflowDetailsDialog
        isOpen={isEditing}
        onOpenChange={setIsEditing}
        workflowName={workflowName}
        setWorkflowName={setWorkflowName}
        workflowDescription={workflowDescription}
        setWorkflowDescription={setWorkflowDescription}
        onSave={handleEditSave}
      />
      <ChannelSelectionDialog
        isOpen={isChannelModalOpen}
        onOpenChange={setIsChannelModalOpen}
        tempChannels={tempChannels}
        handleChannelToggle={handleChannelToggle}
        channelIcons={channelIcons}
        onSave={handleChannelModalSave}
        trigger={tempTrigger}
        nodes={tempNodes}
      />

      <WorkflowSettingsDialog
        isOpen={isSettingsModalOpen}
        onOpenChange={setIsSettingsModalOpen}
        workflowFirstActivatedAt={workflowFirstActivatedAt}
        mode='edit'
        onSave={handleSettingsSave}
      />
    </div>
  );
};

export default EditWorkflowPage;
